/**
 * Table Editor
 *
 * Main editing panel for a selected table.
 * Includes table metadata, columns list with drag-drop, and relationship builder.
 */

import React from "react";
import Box from "@mui/joy/Box";
import Input from "@mui/joy/Input";
import Textarea from "@mui/joy/Textarea";
import Button from "@mui/joy/Button";
import Typography from "@mui/joy/Typography";
import Divider from "@mui/joy/Divider";
import Card from "@mui/joy/Card";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import FormHelperText from "@mui/joy/FormHelperText";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import ViewColumnRoundedIcon from "@mui/icons-material/ViewColumnRounded";
import {
  DragDropContext,
  Droppable,
  Draggable,
  type DropResult,
} from "@hello-pangea/dnd";
import { ColumnEditor } from "./ColumnEditor";
import { RelationshipBuilder } from "./RelationshipBuilder";
import type {
  SchemaBuilderTable,
  SchemaBuilderColumn,
  SchemaBuilderRelationship,
  OnDeleteBehavior,
} from "../../types";

interface TableEditorProps {
  table: SchemaBuilderTable;
  allTables: SchemaBuilderTable[];
  relationships: SchemaBuilderRelationship[];
  onUpdateTable: (updates: Partial<SchemaBuilderTable>) => void;
  onAddColumn: () => void;
  onUpdateColumn: (
    columnId: string,
    updates: Partial<SchemaBuilderColumn>,
  ) => void;
  onDeleteColumn: (columnId: string) => void;
  deletingColumnIds?: ReadonlySet<string>;
  onReorderColumns: (columns: SchemaBuilderColumn[]) => void;
  onAddRelationship: (
    sourceTableId: string,
    sourceColumnId: string,
    targetTableId: string,
    targetColumnId: string,
    onDelete?: OnDeleteBehavior,
  ) => void;
  onDeleteRelationship: (relationshipId: string) => void;
}

export function TableEditor({
  table,
  allTables,
  relationships,
  onUpdateTable,
  onAddColumn,
  onUpdateColumn,
  onDeleteColumn,
  deletingColumnIds = new Set(),
  onReorderColumns,
  onAddRelationship,
  onDeleteRelationship,
}: TableEditorProps) {
  const handleDisplayNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const displayName = e.target.value;
    // Auto-generate snake_case name from display name
    const name = displayName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_|_$/g, "");
    onUpdateTable({ displayName, name });
  };

  const handleDragEnd = (result: DropResult) => {
    if (!result.destination) return;

    // Get only the sortable (non-PK) columns in their current sorted order
    const pkCol = table.columns.find((c) => c.isPrimaryKey);
    const nonPkCols = table.columns
      .filter((c) => !c.isPrimaryKey)
      .sort((a, b) => a.sortOrder - b.sortOrder);

    // Reorder the non-PK columns
    const [reorderedItem] = nonPkCols.splice(result.source.index, 1);
    nonPkCols.splice(result.destination.index, 0, reorderedItem);

    // Update sort orders: PK stays at 0, others start at 1
    const updatedColumns = [
      ...(pkCol ? [{ ...pkCol, sortOrder: 0 }] : []),
      ...nonPkCols.map((col, index) => ({
        ...col,
        sortOrder: index + 1,
      })),
    ];

    onReorderColumns(updatedColumns);
  };

  // Filter relationships for this table
  const tableRelationships = relationships.filter(
    (r) => r.sourceTableId === table.tempId || r.targetTableId === table.tempId,
  );

  // Separate primary key column (pinned at top) from other columns (sortable)
  const pkColumn = table.columns.find((c) => c.isPrimaryKey);
  const sortableColumns = table.columns
    .filter((c) => !c.isPrimaryKey)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  return (
    <Box sx={{ flex: 1, p: 3, overflow: "auto" }}>
      <Box sx={{ maxWidth: 800 }}>
        {/* Table metadata */}
        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Typography level="title-md" sx={{ mb: 2 }}>
            Table Details
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControl>
              <FormLabel>Table Name</FormLabel>
              <Input
                placeholder="e.g., Customers, Orders, Products"
                value={table.displayName}
                onChange={handleDisplayNameChange}
              />
              <FormHelperText>
                Internal name:{" "}
                {table.name || "(auto-generated from display name)"}
              </FormHelperText>
            </FormControl>

            <FormControl>
              <FormLabel>Description (optional)</FormLabel>
              <Textarea
                placeholder="What kind of data will this table store?"
                value={table.description}
                onChange={(e) => onUpdateTable({ description: e.target.value })}
                minRows={2}
              />
            </FormControl>
          </Box>
        </Card>

        {/* Columns section */}
        <Card variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Typography
              level="title-md"
              startDecorator={<ViewColumnRoundedIcon />}
            >
              Columns
            </Typography>
            <Button
              size="sm"
              variant="soft"
              startDecorator={<AddRoundedIcon />}
              onClick={onAddColumn}
            >
              Add Column
            </Button>
          </Box>

          <Typography level="body-sm" sx={{ mb: 2, color: "text.secondary" }}>
            Drag columns to reorder. The ID column is pinned at the top as the
            primary key.
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {/* Primary key column - pinned at top, not draggable */}
            {pkColumn && (
              <ColumnEditor
                column={pkColumn}
                onUpdate={(updates) => onUpdateColumn(pkColumn.tempId, updates)}
                onDelete={() => onDeleteColumn(pkColumn.tempId)}
                isDeleting={deletingColumnIds.has(pkColumn.tempId)}
                dragHandleProps={undefined}
                isDragging={false}
              />
            )}

            {/* Sortable columns */}
            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="columns">
                {(provided) => (
                  <Box
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                    sx={{ display: "flex", flexDirection: "column", gap: 1 }}
                  >
                    {sortableColumns.map((column, index) => (
                      <Draggable
                        key={column.tempId}
                        draggableId={column.tempId}
                        index={index}
                      >
                        {(provided, snapshot) => (
                          <Box
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                          >
                            <ColumnEditor
                              column={column}
                              onUpdate={(updates) =>
                                onUpdateColumn(column.tempId, updates)
                              }
                              onDelete={() => onDeleteColumn(column.tempId)}
                              isDeleting={deletingColumnIds.has(column.tempId)}
                              dragHandleProps={
                                provided.dragHandleProps || undefined
                              }
                              isDragging={snapshot.isDragging}
                            />
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>

          {table.columns.length === 1 && (
            <Box
              sx={{
                mt: 2,
                p: 2,
                borderRadius: "sm",
                bgcolor: "background.level1",
                textAlign: "center",
              }}
            >
              <Typography level="body-sm" sx={{ color: "text.secondary" }}>
                Add columns to define what data this table will store.
              </Typography>
            </Box>
          )}
        </Card>

        {/* Relationships section */}
        <Divider sx={{ my: 3 }} />

        <RelationshipBuilder
          currentTable={table}
          allTables={allTables}
          relationships={tableRelationships}
          onAddRelationship={onAddRelationship}
          onDeleteRelationship={onDeleteRelationship}
        />
      </Box>
    </Box>
  );
}

export default TableEditor;
